import { FC, useCallback, useEffect, useState } from "react";
import { TDepositHistoryStatus } from "@/context/GummiwormContext/hooks/useDepositTxs";
import { useBrandName } from "@/hooks/useBrandName";

interface ProcessingStepProps {
  requestId: string | null;
  status: TDepositHistoryStatus | null;
  error: string | null;
  estimatedAbsorptionTime?: number;
  amountLabel?: string;
  txHash?: string;
}

const STAGES = [
  { label: "Approved", activeLabel: "Requesting" },
  { label: "Submitted", activeLabel: "Submitting" },
  { label: "Available", activeLabel: "Waiting for Settlement" },
];

/** Map backend status to the stage index that is currently active.
 *  Returns 3 when all stages are complete (absorbed).
 *
 *  Stage 0 – Requesting → Approved once the deposit is accepted
 *  Stage 1 – Submitting → Submitted once the L1 tx is confirmed
 *  Stage 2 – Waiting for Settlement → Available once absorbed
 */
function getActiveStageIndex(status: TDepositHistoryStatus | null): number {
  switch (status) {
    case null:
    case "new":
    case "pending":
      return 0; // requesting / awaiting approval
    case "confirmed":
    case "settling":
      return 1; // approved, submitting L1 tx
    case "on-chain":
      return 2; // L1 confirmed, waiting for absorption
    case "absorbed":
      return 3; // all done
    default:
      return 0;
  }
}

function CopyButton({ text }: { text: string }) {
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="text-sr-pink-muted hover:text-sr-pink-light transition-colors"
      title="Copy"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
    </button>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="#170B10"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type BadgeVariant = "done" | "active" | "waiting" | "failed";

function StatusBadge({
  variant,
  label,
  className,
}: {
  variant: BadgeVariant;
  label?: string;
  className?: string;
}) {
  const config = {
    done: { label: "Done", className: "bg-sr-mint/15 text-sr-mint" },
    active: { label: "In progress", className: "bg-sr-tangerine/15 text-sr-tangerine" },
    waiting: { label: "Waiting", className: "bg-sr-neutral-700/30 text-sr-neutral-700" },
    failed: { label: "Failed", className: "bg-sr-salmon/15 text-sr-salmon" },
  };
  const { label: defaultLabel, className: baseClassName } = config[variant];
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${baseClassName} ${className ?? ""}`}>
      {label ?? defaultLabel}
    </span>
  );
}

function formatCountdown(seconds: number): string {
  if (seconds <= 0) return "any moment now";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m > 0) return `~${m}m ${s.toString().padStart(2, "0")}s`;
  return `~${s}s`;
}

export const ProcessingStep: FC<ProcessingStepProps> = ({
  requestId,
  status,
  error,
  estimatedAbsorptionTime,
  amountLabel,
  txHash,
}) => {
  const brandName = useBrandName();
  const isFailed = status === "failed" || status === "rejected";
  const isComplete = status === "absorbed";
  const activeIdx = getActiveStageIndex(status);
  const explorerHref = txHash
    ? `https://${window.__APP_CONFIG.env === "mainnet" ? "" : "preview."}cexplorer.io/tx/${txHash}`
    : null;
  const statusDetail = isFailed
    ? { variant: "failed" as const, label: "Failed" }
    : isComplete
      ? { variant: "done" as const, label: "Available" }
      : activeIdx === 0
        ? { variant: "active" as const, label: "Requesting" }
        : activeIdx === 1
          ? { variant: "active" as const, label: "Submitting" }
          : { variant: "active" as const, label: "Waiting for Settlement" };

  // Live countdown
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!estimatedAbsorptionTime) {
      setRemaining(null);
      return;
    }
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      setRemaining(Math.max(0, estimatedAbsorptionTime - now));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [estimatedAbsorptionTime]);

  return (
    <div className="sr-card w-full rounded-[32px] px-4 pt-3 pb-6 sm:p-8">
      {/* Header row */}
      <div className="flex items-start justify-between mb-4">
        <h2
          className="text-[22px] font-medium text-white tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {isFailed
            ? "Deposit Failed"
            : isComplete
              ? "Funds Deposited!"
              : "Deposit in Progress"}
        </h2>
      </div>

      {/* Deposit info */}
      {amountLabel && (
        <div className="mb-8">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-sr-pink-muted">
              Amount
            </span>
            <p
              className="text-[32px] font-medium leading-none tracking-tight text-white sm:text-[38px]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {amountLabel}
            </p>
          </div>

          <div className="mt-6 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 sm:gap-x-8">
            <span className="text-sm text-sr-pink-muted">To</span>
            <span className="text-sm font-medium text-sr-bubblegum">
              {brandName === "Sugar Rush" ? "Sugar Rush Trading Vault" : "Gummiworm Vault"}
            </span>

            <span className="text-sm text-sr-pink-muted">Status</span>
            <div className="flex items-center">
              <StatusBadge
                variant={statusDetail.variant}
                label={statusDetail.label}
              />
            </div>

            {requestId && (
              <>
                <span className="text-sm text-sr-pink-muted">Request ID</span>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-white">
                    {requestId}
                  </span>
                  <CopyButton text={requestId} />
                </div>
              </>
            )}

            {txHash && (
              <>
                <span className="text-sm text-sr-pink-muted">Transaction</span>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-white">
                    {txHash.slice(0, 8)}...{txHash.slice(-8)}
                  </span>
                  <CopyButton text={txHash} />
                  {explorerHref && (
                    <a
                      href={explorerHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sr-pink-muted hover:text-sr-pink-light transition-colors"
                    >
                      <ExternalLinkIcon />
                    </a>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Countdown */}
      {!isFailed && !isComplete && remaining !== null && (
        <div className="flex items-center gap-2 mb-6">
          <span
            className="text-lg font-medium text-sr-lemon-muted tabular-nums"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {formatCountdown(remaining)}
          </span>
          <span className="text-xs text-sr-neutral-500">
            estimated time remaining
          </span>
        </div>
      )}

      {/* Vertical stepper */}
      <div className="flex flex-col">
        {STAGES.map((stage, i) => {
          const isDone = activeIdx > i;
          const isActive = activeIdx === i && !isFailed;
          const isFuture = !isDone && !isActive && i !== STAGES.length - 1;
          const variant: BadgeVariant = isDone
            ? "done"
            : isActive
              ? "active"
              : "waiting";

          return (
            <div key={i} className="flex items-center gap-4">
              {/* Icon + connector */}
              <div className="flex flex-col items-center self-stretch">
                {isDone ? (
                  <div className="w-8 h-8 rounded-full bg-sr-mint/70 flex items-center justify-center shrink-0">
                    <CheckIcon />
                  </div>
                ) : isActive ? (
                  <div className="w-8 h-8 rounded-full border-2 border-sr-tangerine border-t-transparent animate-spin shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full border border-sr-neutral-700/70 shrink-0" />
                )}
                {i < STAGES.length - 1 && (
                  <div
                    className={`w-px flex-1 min-h-[24px] ${
                      isDone ? "bg-sr-mint/15" : "bg-sr-neutral-700/20"
                    }`}
                  />
                )}
              </div>

              {/* Content */}
              <div className={`flex-1 flex items-center min-h-8 ${i < STAGES.length - 1 ? "pb-5" : ""}`}>
                <div className="flex w-full items-center justify-between">
                  <span
                    className={`text-sm font-medium ${
                      isActive
                        ? "text-white"
                        : isDone
                          ? "text-white/60"
                          : "text-sr-neutral-700/70"
                    }`}
                  >
                    {isActive ? stage.activeLabel : stage.label}
                  </span>
                  {i === STAGES.length - 1 && isComplete ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sr-mint/15 text-sr-mint">
                      Complete!
                    </span>
                  ) : (
                    <StatusBadge
                      variant={variant}
                      className={
                        isDone
                          ? "opacity-70"
                          : isFuture
                            ? "opacity-45"
                            : ""
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {isFailed && error && (
        <div className="mt-6 rounded-xl bg-sr-salmon/10 p-3">
          <p className="text-xs text-sr-salmon">{error}</p>
        </div>
      )}
    </div>
  );
};
